import { useLocale, useTranslations } from 'next-intl';
import { Fragment } from 'react';

import { Course, Courseversion } from '@/payload/payload-types';

import { CourseVersionSelect } from '../../../_components/CourseVersionSelect';

import dayjs from 'dayjs';
import 'dayjs/locale/et';
import 'dayjs/locale/en';
import Clock from '../../../_icons/Clock';

type CourseversionTimeEdit = Courseversion['timeEdit'];
type CourseversionCalendar = NonNullable<CourseversionTimeEdit['calendar']>;
export const TimeManagement: React.FC<{
	courseversion: Courseversion;
	course: Course;
}> = props => {
	const t = useTranslations('Course');
	const locale = useLocale();

	dayjs.locale(locale);

	const { courseversion } = props;
	const { classroomMeetingCount, individualWorkCount } = courseversion;

	if (!courseversion.timeEdit.calendar?.length) {
		return null;
	}
	// Group lessons by month
	const groupedLessons: { [key: string]: CourseversionCalendar } = {};
	courseversion.timeEdit.calendar.forEach(timeSlot => {
		const monthKey = dayjs(timeSlot.startDate).format('MMMM YYYY');
		if (!groupedLessons[monthKey]) {
			groupedLessons[monthKey] = [];
		}
		groupedLessons[monthKey].push(timeSlot);
	});

	const allHours = (classroomMeetingCount ?? 0) + (individualWorkCount ?? 0);
	const contactHours = classroomMeetingCount ?? 0;
	const independentHours = individualWorkCount ?? 0;

	let descriptionKey:
		| 'time-management-description-no-contactHours'
		| 'time-management-description-no-independentHours'
		| 'time-management-description';

	if (contactHours === 0 && independentHours > 0) {
		descriptionKey = 'time-management-description-no-contactHours';
	} else if (contactHours > 0 && independentHours === 0) {
		descriptionKey = 'time-management-description-no-independentHours';
	} else {
		descriptionKey = 'time-management-description';
	}

	return (
		<div className="flex flex-col space-y-12">
			<h1 className="text-muted-navy m-0 text-5xl font-bold">{t('time-management-title')}</h1>

			<CourseVersionSelect />

			<p>
				{t(descriptionKey, {
					allHours: allHours,
					contactHours: contactHours,
					independentHours: independentHours,
				})}
			</p>

			<div className="flex flex-col space-y-6">
				{Object.entries(groupedLessons).map(([monthKey, lessons]) => (
					<div className="space-y-2" key={monthKey}>
						<p className="text-base leading-6 font-bold capitalize">{monthKey}</p>
						<div className="flex flex-col space-y-2">
							{lessons.map(timeSlot => (
								<div
									className="flex w-full max-w-[432px] gap-x-4 rounded-xl px-3 py-2 shadow-[0_0_12px_0_rgba(0,0,0,0.06)] lg:px-4 lg:py-3"
									key={timeSlot.reservationId}
								>
									<div className="flex w-1/2 flex-col lg:w-1/3 lg:flex-row">
										<p className="text-base leading-8 capitalize">{`${dayjs(timeSlot.startDate).format('dddd')}`}</p>
										<p className="text-base leading-8 lg:hidden">{`${dayjs(timeSlot.startDate).format('DD.MM.YYYY')}`}</p>
									</div>

									<p className="hidden text-base leading-8 lg:flex lg:w-1/3">{`${dayjs(timeSlot.startDate).format('DD.MM.YYYY')}`}</p>

									<p className="w-1/2 text-base leading-8 lg:w-1/3">
										{timeSlot.hasExactTime ? (
											`${dayjs(timeSlot.startDate).format('HH:mm')} - ${dayjs(timeSlot.endDate).format('HH:mm')}`
										) : (
											<Fragment>
												<span className="inline-flex items-center">
													<Clock className="mr-1" />
													{t('time-tbd')}
												</span>
											</Fragment>
										)}
									</p>
								</div>
							))}
						</div>
					</div>
				))}
			</div>

			<p className="mt-12 text-sm">*{t('schedule-might-change')}</p>
		</div>
	);
};
